"use client"

import * as React from "react"

/* The wheel's lamps around the hand. Two arcs, one over the cards and one
   under, joined into a ring so a chase runs round: along the top left to
   right, down the right side, along the bottom right to left, back up.
   Same socket, lens and lit heart as the wheel; same programs, timed to
   the pack's beats instead of the spin's.

   Programs:
   - attract: the wheel's three rest patterns in rotation (comet, two comets
     meeting, fill-up round the ring).
   - kick:    every lamp on, the press.
   - chase:   a comet running fast round the ring (the deal, the flight).
   - build:   the silent beat. Lamps fill from the bottom centre round both
     ways to the top centre over the beat, the frontier brightest.
   - wave:    the turn. All on, a wave out from the top centre both ways,
     hold, blink twice, then celebrate.
   - celebrate: every lamp half on with a comet running over it.
   - off:     dark. */

export type LampProgram = "attract" | "kick" | "chase" | "build" | "wave" | "celebrate" | "off"

const PER_ARC = 20
const COUNT = PER_ARC * 2
const ATTRACT: { stepMs: number; steps: number }[] = [
  { stepMs: 900, steps: 20 },
  { stepMs: 640, steps: 24 },
  { stepMs: 150, steps: 52 },
]
const CHASE_STEP_MS = 170
const WAVE_STEP_MS = 60
const mod = (n: number, m: number) => ((n % m) + m) % m

export interface CardLampsProps {
  program: LampProgram
  /** Length of the build, so the fill reaches the top exactly at the turn. */
  buildMs?: number
  stageW: number
  /** y of the top arc at its centre and of the bottom arc at its centre. */
  topY: number
  bottomY: number
  /** Half the span of the arcs, from the centre out. */
  halfW: number
  /** Sag of each arc: extra y at the ends. */
  topSag: number
  bottomSag: number
}

export function CardLamps({ program, buildMs = 2600, stageW, topY, bottomY, halfW, topSag, bottomSag }: CardLampsProps) {
  const uid = React.useId().replace(/:/g, "")
  const [chase, setChase] = React.useState(0)
  const [attract, setAttract] = React.useState(0)
  /** -1: nothing yet; 0..PER_ARC: the wave front; 100: all on; 101: all off (blink). */
  const [waveStep, setWaveStep] = React.useState(-1)
  const [build, setBuild] = React.useState(0)
  const [afterWave, setAfterWave] = React.useState(false)
  const timers = React.useRef<number[]>([])

  React.useEffect(() => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
    const later = (fn: () => void, ms: number) => {
      const t = window.setTimeout(fn, ms)
      timers.current.push(t)
    }
    setAfterWave(false)

    if (program === "attract") {
      let pattern = 0
      let step = 0
      setAttract(0)
      setChase(0)
      const tick = () => {
        const p = ATTRACT[pattern]
        step += 1
        if (step >= p.steps) {
          step = 0
          pattern = (pattern + 1) % ATTRACT.length
          setAttract(pattern)
        }
        setChase(step)
        later(tick, ATTRACT[pattern].stepMs)
      }
      later(tick, ATTRACT[0].stepMs)
      return
    }
    if (program === "chase" || program === "celebrate") {
      const tick = () => {
        setChase((c) => c + 1)
        later(tick, CHASE_STEP_MS)
      }
      later(tick, CHASE_STEP_MS)
      return
    }
    if (program === "build") {
      const t0 = performance.now()
      let raf = 0
      const frame = () => {
        const p = Math.min(1, (performance.now() - t0) / buildMs)
        // Eases in: slow to start, quickening to the top.
        setBuild(p * p)
        if (p < 1) raf = requestAnimationFrame(frame)
      }
      raf = requestAnimationFrame(frame)
      return () => cancelAnimationFrame(raf)
    }
    if (program === "wave") {
      // A wave out from the top both ways, a hold with every lamp warm,
      // then the celebration chase takes over. No flash, no blink.
      setWaveStep(0)
      for (let s = 1; s <= PER_ARC; s++) later(() => setWaveStep(s), s * WAVE_STEP_MS)
      const held = PER_ARC * WAVE_STEP_MS + 200
      later(() => setWaveStep(100), held)
      later(() => setAfterWave(true), held + 1400)
      return
    }
    return () => timers.current.forEach((t) => window.clearTimeout(t))
  }, [program, buildMs])

  React.useEffect(() => {
    if (!afterWave) return
    const tick = () => {
      setChase((c) => c + 1)
      const t = window.setTimeout(tick, CHASE_STEP_MS)
      timers.current.push(t)
    }
    tick()
  }, [afterWave])

  React.useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), [])

  /** Lamp k's distance round the ring from the top centre, 0..PER_ARC. */
  const fromTop = (k: number) => {
    if (k < PER_ARC) return Math.abs(k - (PER_ARC - 1) / 2)
    return PER_ARC / 2 + Math.abs(k - PER_ARC - (PER_ARC - 1) / 2)
  }

  const level = (k: number): number => {
    // Everything here sits under the wheel's levels: the peak is 0.8, the
    // tails are long and nothing goes from dark to full in one step.
    const PEAK = 0.8
    const WARM = 0.3
    const comet = (len: number) => {
      const d = mod(k - chase, len)
      return d === 0 ? PEAK : d === len - 1 ? 0.5 : d === len - 2 ? 0.25 : d === len - 3 ? 0.1 : 0
    }
    const celebrate = () => {
      const d = mod(k - chase, 6)
      return d === 0 ? PEAK : d === 5 ? 0.55 : d === 4 ? 0.4 : WARM
    }
    switch (program) {
      case "off":
        return 0
      case "kick":
        return PEAK
      case "chase":
        return comet(6)
      case "build": {
        // Fill from the bottom centre round to the top: the frontier moves
        // from the farthest lamps (bottom centre) toward the top centre.
        const far = PER_ARC - fromTop(k)
        const front = build * PER_ARC
        if (far > front) return 0
        return front - far < 1 ? PEAK : front - far < 2 ? 0.5 : WARM
      }
      case "wave": {
        if (afterWave) return celebrate()
        if (waveStep < 0) return 0
        if (waveStep === 100) return 0.55
        const d = fromTop(k)
        if (d > waveStep) return 0
        return Math.abs(d - waveStep) < 1 ? PEAK : Math.abs(d - waveStep) < 2 ? 0.55 : 0.4
      }
      case "celebrate":
        return celebrate()
      default: {
        switch (attract) {
          case 0:
            return comet(6)
          case 1: {
            const a = mod(k - chase, 8)
            const b = mod(k + chase, 8)
            const near = Math.min(a, 8 - a, b, 8 - b)
            return near === 0 ? PEAK : near === 1 ? 0.3 : 0
          }
          default: {
            const lit = chase % (COUNT + 12)
            if (k >= lit) return 0
            return k === lit - 1 ? PEAK : k === lit - 2 ? 0.5 : WARM
          }
        }
      }
    }
  }

  /** Lamp positions: the top arc left to right, the bottom arc right to
      left, so the index runs round the ring. */
  const cx = stageW / 2
  const spots: [number, number][] = []
  for (let i = 0; i < PER_ARC; i++) {
    const u = (i / (PER_ARC - 1)) * 2 - 1
    spots.push([cx + u * halfW, topY + topSag * u * u])
  }
  for (let i = 0; i < PER_ARC; i++) {
    const u = 1 - (i / (PER_ARC - 1)) * 2
    spots.push([cx + u * halfW, bottomY + bottomSag * u * u])
  }

  const lensId = `${uid}-lens`
  const glowId = `${uid}-glow`

  return (
    // Not on the table at all until they are needed: sockets and lenses
    // fade in with the first program and leave with "off".
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ opacity: program === "off" ? 0 : 1, transition: "opacity 900ms ease-out" }}
    >
      <defs>
        <radialGradient id={lensId} cx="38%" cy="34%" r="70%">
          <stop offset="0" stopColor="hsl(var(--white-500))" />
          <stop offset="0.55" stopColor="hsl(var(--white-300))" />
          <stop offset="1" stopColor="hsl(var(--white-100))" />
        </radialGradient>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="hsl(var(--white-0))" stopOpacity="1" />
          <stop offset="0.22" stopColor="hsl(var(--white-0))" stopOpacity="0.9" />
          <stop offset="0.4" stopColor="hsl(var(--primary-0))" stopOpacity="0.45" />
          <stop offset="1" stopColor="hsl(var(--primary-0))" stopOpacity="0" />
        </radialGradient>
      </defs>
      {spots.map(([bx, by], k) => {
        const l = level(k)
        // On in a breath, off slowly: a warm bulb, not a strobe.
        const fade = l >= 0.8 ? "opacity 180ms ease-out" : l > 0 ? "opacity 360ms ease-out" : "opacity 800ms ease-out"
        return (
          <g key={k}>
            <circle cx={bx} cy={by} r={7} fill="hsl(var(--grey-950))" stroke="hsl(var(--white-100))" strokeWidth={1} />
            <circle cx={bx} cy={by} r={4.5} fill={`url(#${lensId})`} />
            <circle cx={bx} cy={by} r={16} fill={`url(#${glowId})`} style={{ opacity: l, transition: fade }} />
          </g>
        )
      })}
    </svg>
  )
}
